/**
 * Shrink a photo in the browser before it is uploaded.
 *
 * A phone camera picture is 10-20 MB and several thousand pixels wide. Every surface that
 * takes one here displays it at avatar or receipt size, so the full-resolution original is
 * uploaded, stored and paid for without ever being looked at — and on a mobile connection it
 * is also 8-19 seconds the user spends waiting before anything can go wrong.
 *
 * Anything this cannot handle is returned untouched, so the upload still happens: a PDF, an
 * animated GIF, an SVG, a format the browser cannot decode, or a picture that is already
 * small enough. Nothing here is a substitute for server-side validation.
 */

const PASS_THROUGH_TYPES = [
  'image/gif', // downscaling would drop the animation
  'image/svg+xml', // vector: there is nothing to downscale
];

async function decode(file) {
  if (window.createImageBitmap) {
    try {
      // `from-image` applies the EXIF rotation, which a phone photo relies on.
      return await window.createImageBitmap(file, {imageOrientation: 'from-image'});
    }
    catch (error) {
      // Safari has refused this for some sources; fall through to the <img> path.
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The browser could not decode this image.'));
    };
    image.src = url;
  });
}

function jpegName(name) {
  return `${String(name || 'image').replace(/\.[^.]+$/, '')}.jpg`;
}

function toBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/**
 * @param {File} file
 * @param {{longestEdge?: number, quality?: number}} options
 * @returns {Promise<File>} the downscaled image, or the original file when it was left alone
 */
export async function downscaleImage(file, options = {}) {
  const longestEdge = options.longestEdge || 1600;
  const quality = options.quality || 0.8;

  if (!file || !file.type || !file.type.startsWith('image/')) {
    return file;
  }

  if (PASS_THROUGH_TYPES.includes(file.type)) {
    return file;
  }

  let source;
  try {
    source = await decode(file);
  }
  catch (error) {
    return file;
  }

  const width = source.width || source.naturalWidth;
  const height = source.height || source.naturalHeight;
  const longest = Math.max(width, height);

  // Already within bounds: hand back the original rather than re-encoding it, which would
  // cost quality and could even make the file bigger.
  if (!longest || longest <= longestEdge) {
    if (source.close) source.close();

    return file;
  }

  const scale = longestEdge / longest;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);

  const context = canvas.getContext('2d');
  // JPEG has no alpha channel, so a transparent PNG would otherwise flatten onto black.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0, canvas.width, canvas.height);

  if (source.close) source.close();

  const blob = await toBlob(canvas, quality);

  if (!blob || blob.size >= file.size) {
    return file;
  }

  return new File([blob], jpegName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * Livewire's own `wire:model` handler on a file input announces an upload with these events,
 * and `wire:loading wire:target="<property>"` listens for them. Driving the upload ourselves
 * means announcing it ourselves, or every existing loading indicator goes quiet.
 */
function announce(input, wire, property, name, detail = {}) {
  input.dispatchEvent(new CustomEvent(name, {
    bubbles: true,
    detail: {id: wire.$id, property, ...detail},
  }));
}

/**
 * Downscale the picked file, refuse it if it is still too large, and hand what is left to
 * Livewire. Replaces `wire:model` on a file input; the property keying, the validation
 * messages and the loading indicators all stay where they were.
 *
 * @param {object} options
 * @param {HTMLInputElement} options.input
 * @param {object} options.wire the Livewire `$wire` object of the surrounding component
 * @param {string} options.property the property to upload to, fixed or dynamically keyed
 * @param {number} options.maxKilobytes the same ceiling the validation rule enforces
 * @param {number} [options.longestEdge]
 * @param {number} [options.quality]
 */
export async function uploadDownscaledImage(options) {
  const {input, wire, property, maxKilobytes} = options;
  const picked = input.files && input.files[0];

  if (!picked) {
    return;
  }

  const file = await downscaleImage(picked, {
    longestEdge: options.longestEdge,
    quality: options.quality,
  });

  // Downscaling is a courtesy the browser may not manage — a PDF, an unsupported format, an
  // image that was already small. Tell the user now rather than after a long upload that
  // dies somewhere they cannot see.
  if (file.size > maxKilobytes * 1024) {
    input.value = '';
    await wire.$call('imageUploadTooLarge', property);

    return;
  }

  announce(input, wire, property, 'livewire-upload-start');

  await new Promise((resolve) => {
    wire.$upload(
      property,
      file,
      () => {
        announce(input, wire, property, 'livewire-upload-finish');
        resolve();
      },
      () => {
        announce(input, wire, property, 'livewire-upload-error');
        resolve();
      },
      (progressEvent) => announce(input, wire, property, 'livewire-upload-progress', {
        progress: Math.round((progressEvent.loaded * 100) / progressEvent.total),
      }),
      () => {
        announce(input, wire, property, 'livewire-upload-cancel');
        resolve();
      },
    );
  });
}

/**
 * `wire:model` on a file input forgets the picked file once the component clears the property,
 * and replacing it with our own upload handler loses that — the filename stays in the control
 * after Cancel. The `$wire` magic hands back a different proxy than the component instance and
 * watching it never fires, so this registers against the component itself.
 */
export function resetFileInputWhenCleared(input, property) {
  const root = input.closest('[wire\\:id]');

  if (!root || !window.Livewire) {
    return;
  }

  const component = window.Livewire.find(root.getAttribute('wire:id'));

  if (!component) {
    return;
  }

  component.$watch(property, (value) => {
    if (input.isConnected && (value === null || value === '')) {
      input.value = '';
    }
  });
}

window.downscaleImage = downscaleImage;
window.uploadDownscaledImage = uploadDownscaledImage;
window.resetFileInputWhenCleared = resetFileInputWhenCleared;

export default downscaleImage;
